import type { FC } from "react";

import otherAssetsGraphic from "@/shared/assets/illustrations/other-assets.svg";

import type { FlowScreenConfig, FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  OtherAssetsChecklist,
  ALL_CHECKLIST_KEYS,
  NONE_VALUE,
} from "../components/OtherAssetsChecklist";
import { OtherAssetsReview } from "../components/OtherAssetsReview";
import { AddOtherAsset } from "../components/AddOtherAsset";
import { createModuleComplete } from "@/pages/postloss/modules/components/ModuleComplete";

function createAddWrapper(assetKey: string): FC<FlowCustomComponentProps> {
  const Wrapper: FC<FlowCustomComponentProps> = (props) => (
    <AddOtherAsset {...props} assetKey={assetKey} />
  );
  return Wrapper;
}

function createAddScreen(assetKey: string): FlowScreenConfig {
  return {
    id: `add_other_asset_${assetKey}`,
    layout: "full",
    maxWidth: "4xl",
    hideButton: true,
    customComponent: createAddWrapper(assetKey),
    shouldDisplay: ({ answers }) => {
      const selected = Array.isArray(answers["other_assets_selected"])
        ? answers["other_assets_selected"]
        : [];
      return (
        selected.includes(assetKey) &&
        !selected.includes(NONE_VALUE) &&
        answers[`other_asset_${assetKey}_done`] !== "yes" &&
        answers[`other_asset_${assetKey}_done`] !== "skipped"
      );
    },
  };
}

export function createOtherAssetsScreens(firstName: string): FlowScreenConfig[] {
  return [
    {
      id: "other_assets_intro",
      layout: "full",
      imageSrc: otherAssetsGraphic,
      imageSize: "md",
      imagePosition: "right",
      verticalCenter: true,
      sectionLabel: "Section 5",
      maxWidth: "4xl",
      title: `Let's discover any other types of assets ${firstName} owned`,
      subtitle:
        "These don't come up in every estate, but when they do, they can be significant. We'll walk through each one.",
      getButtonText: () => "Get started",
    },
    {
      id: "other_assets_checklist",
      layout: "full",
      textAlign: "center",
      maxWidth: "2xl",
      title: `Did ${firstName} have any of these other assets?`,
      subtitle: `These don't come up in every estate. Select anything that applies to ${firstName}.`,
      hideButton: true,
      customComponent: OtherAssetsChecklist,
    },
    ...ALL_CHECKLIST_KEYS.map((key) => createAddScreen(key)),
    {
      id: "other_assets_review",
      layout: "full",
      textAlign: "center",
      maxWidth: "2xl",
      title: `Review ${firstName}'s other assets`,
      subtitle: "Here's what we have. You can always come back to add or edit.",
      hideButton: true,
      customComponent: OtherAssetsReview,
      shouldDisplay: ({ answers }) => {
        const selected = Array.isArray(answers["other_assets_selected"])
          ? answers["other_assets_selected"]
          : [];
        const realSelections = selected.filter((v) => v !== NONE_VALUE);
        if (realSelections.length === 0) return false;
        return realSelections.some((key) => answers[`other_asset_${key}_done`] === "yes");
      },
    },
    {
      id: "final_screen",
      layout: "full",
      textAlign: "center",
      hideButton: true,
      headerTitle: "",
      customComponent: createModuleComplete(),
    },
  ];
}
